import { useCallback, useEffect, useState } from 'react'
import { useLocation, useParams } from 'react-router'
import MainProduct from './Tabs/MainProduct'
import { getProductName, parseSelectedCategories } from './function'
import { ComponentWithPermissions } from '../../../../../../../baseComponents/CreateRoutering/types'
import EditCardTabs from '../../../../../../../baseComponents/EditCardTabs'
import { twinFetchPostJSON } from '../../../../../../../utils/globals/data'
import { getBasePath } from '../../../../../../../utils/globals/link'
import { ComponentTabObject } from '../../../../../../../utils/hooks/useTabLogic'
import useTwinTranslation from '../../../../../../../utils/hooks/useTwinTranslation'
import { setVariablesToCurrentPage } from '../../../../../../../utils/reducers/reduxDispatch'
import LotProducts from '../../../../../../../specificComponents/Product/ProductLayout/Tabs/LotProducts'
import FeaturesProduct from '../../../../../../../specificComponents/Product/ProductLayout/Tabs/FeaturesProduct'
import OtherInformationConnect from '../../../../../../../specificComponents/Product/ProductLayout/Tabs/OtherInformation'
import ProductAddon from '../../../../../../../specificComponents/Product/ProductLayout/Tabs/ProductAddon'
import ProductComposed from '../../../../../../../specificComponents/Product/ProductLayout/Tabs/ProductComposed'
import SeoProduct from '../../../../../../../specificComponents/Product/ProductLayout/Tabs/SeoProduct'
import SpecificPricesProduct from '../../../../../../../specificComponents/Product/ProductLayout/Tabs/SpecificPricesProduct'
import StockProduct from '../../../../../../../specificComponents/Product/ProductLayout/Tabs/StockProduct'
import SuppliersProduct from '../../../../../../../specificComponents/Product/ProductLayout/Tabs/SuppliersProduct'
import UniqueProducts from '../../../../../../../specificComponents/Product/ProductLayout/Tabs/UniqueProducts'
import VariantsProduct from '../../../../../../../specificComponents/Product/ProductLayout/Tabs/VariantsProduct'
import { ProductExtendedTyShort } from '../../../../../../../specificComponents/Product/types'
import WrapperProductTabs from '../../../../../../../specificComponents/Product/ProductLayout/Subcomponents/WrapperProductTabs'
import ProductEscandalling from '../../../../../../../specificComponents/Product/ProductLayout/Tabs/ProductEscandalling'
import SellsProduct from '../../../../../../../specificComponents/Product/ProductLayout/Tabs/SellsProduct'

type SingleProductProps = SingleProductLogicProps & ComponentWithPermissions & {}

const SingleProduct: React.FC<SingleProductProps> = ({ userPermissions }) => {
    const location = useLocation()
    const params = useParams<{ id: string, tab?: string }>()
    const { t } = useTwinTranslation()
    const { basicData, getData } = useSingleProductLogic({ params })
    if (!params.id) {
        return null
    }
    const tabs: ComponentTabObject = {
        'main': { component: MainProduct, text: t('general', 'General'), permission: 'product.profile.main' },
        'stock': { component: StockProduct, text: t('stock', 'Inventario'), permission: 'product.profile.stock' },
        'prices': { component: SpecificPricesProduct, text: t('prices', 'Precios'), permission: 'product.profile.specificPrice' },
        'variants': { component: VariantsProduct, text: t('variants', 'Variantes'), permission: 'product.profile.variant' },
        'product-addon': { component: ProductAddon, text: t('addons', 'Complementos'), permission: 'product.profile.productAddon' },
        'product-escandalling': { component: ProductEscandalling, text: t('escandallings', 'Escandallos'), permission: 'product.profile.productAddon' },
        'product-composed': { component: ProductComposed, text: t('productComposed', 'Producto Compuesto'), permission: 'product.profile.productComposed' },
        'suppliers': { component: SuppliersProduct, text: t('suppliers', 'Proveedores'), permission: 'product.profile.supplier' },
        'features': { component: FeaturesProduct, text: t('features', 'Características'), permission: 'product.profile.features' },
        'sells': { component: SellsProduct, text: t('sells', 'Ventas'), permission: 'product.profile.sells', modules: ['quotes'] },
        'other-information': { component: OtherInformationConnect, text: t('otherInformation', 'Otra información'), permission: 'product.profile.otherInformation' },
        'web': { component: SeoProduct, text: t('webShort', 'Web'), permission: 'product.profile.web' },
    }
    if (basicData?.productType === 3) {
        tabs['product-unique'] = { component: UniqueProducts, text: t('serialNumbers', 'Números de serie'), permission: 'product.profile.serialized' }
    }
    if (basicData?.productType === 4) {
        tabs['product-lot'] = { component: LotProducts, text: t('lots', 'Lotes'), permission: 'product.profile.lot' }
    }
    return (<EditCardTabs basePath={getBasePath(location.pathname, params.id)} basicData={basicData} tabs={tabs} tab={params.tab} userPermissions={userPermissions} getData={getData} Wrapper={WrapperProductTabs} />)
}

interface SingleProductLogicProps {
    params: { id?: string, tab?: string }
}


const useSingleProductLogic = ({ params }: SingleProductLogicProps) => {
    const [basicData, setBasicData] = useState<ProductExtendedTyShort | null>(null)

    const id = params.id

    const getData = useCallback(async () => {
        const myData: ProductExtendedTyShort = await twinFetchPostJSON('/api/app/product/getProductInstanceComplete', {
            id,
        })
        if (myData) {
            myData['parsedCategories'] = parseSelectedCategories(myData?.CategoriesProducts)
            myData['name'] = getProductName(myData.ProductLangs)
            setBasicData(myData)
            setVariablesToCurrentPage([myData['name']])
        }
    }, [setBasicData, id])

    useEffect(() => {
        setBasicData(null)
        getData()
    }, [getData])

    return { basicData, getData }
}

export default SingleProduct